// 1:1 port from age-of-mcp v3 (mine.ts). The flattened part literals are
// reconstructed from the original helper functions; geometry is parity-gated
// identical.
//
// v3 mine — a rock mound with a dark adit cut into its front under a timber
// portal and a small slate (ROOF_MINERAL) hood, crowned by a tall timber
// headframe with a steel winding wheel, the signature vertical. An ore cart and
// an ore heap dress the open yard; the miner works at the adit mouth.

import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, gable, poles, slab, HAZARD, ROOF_MINERAL, STEEL, STONE, STONE_DK, TIMBER } = styleV3;

const BASE = 0.12;             // foundation slab top
const MOUND_H = 0.62;          // rock mound height
const TOP = BASE + MOUND_H;    // mound top

// The rock mound the mine is sunk into, `h` tall.
function mound(h: number): Part {
  return box(0.66, h, 0.6, STONE, [0, BASE + h / 2, -0.08]);
}

// The dark adit mouth on the mound's front, framed in timber.
function adit(): Part[] {
  return [
    box(0.28, 0.32, 0.06, HAZARD, [0, BASE + 0.17, 0.24]),
    box(0.4, 0.06, 0.09, TIMBER,  [0, BASE + 0.36, 0.24]),
    box(0.06, 0.34, 0.09, TIMBER, [-0.17, BASE + 0.17, 0.24]),
    box(0.06, 0.34, 0.09, TIMBER, [0.17, BASE + 0.17, 0.24]),
  ];
}

// A small slate hood over the portal — the mineral roof code.
function hood(): Part {
  return gable(0.3, 0.5, 0.12, ROOF_MINERAL, 0, BASE + 0.42, 0.27, "x");
}

// The timber headframe over the shaft, with its steel winding wheel — the
// signature vertical.
function headframe(): Part[] {
  return [
    box(0.05, 0.5, 0.05, TIMBER, [-0.13, TOP + 0.23, -0.1], [0, 0, 0.18]),
    box(0.05, 0.5, 0.05, TIMBER, [0.13, TOP + 0.23, -0.1], [0, 0, -0.18]),
    box(0.34, 0.06, 0.08, TIMBER, [0, TOP + 0.47, -0.1]),
    cyl(0.09, 0.09, 0.04, 8, STEEL, [0, TOP + 0.55, -0.1], [0, 0, Math.PI / 2]),
  ];
}

// A loaded ore cart in the east yard, wheels rolling toward the adit.
function oreCart(): Part[] {
  return [
    box(0.26, 0.14, 0.18, TIMBER,   [0.3, BASE + 0.12, 0.42]),
    box(0.22, 0.08, 0.14, STONE_DK, [0.3, BASE + 0.22, 0.42]),
    cyl(0.05, 0.05, 0.03, 6, STEEL, [0.18, BASE + 0.05, 0.42], [0, 0, Math.PI / 2]),
    cyl(0.05, 0.05, 0.03, 6, STEEL, [0.42, BASE + 0.05, 0.42], [0, 0, Math.PI / 2]),
  ];
}

// The ore heap dug out so far, on the west yard.
function oreHeap(): Part {
  return cyl(0.02, 0.17, 0.18, 7, STONE_DK, [-0.32, BASE + 0.09, 0.36]);
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.88, 0.88)];
  if (s === 1) return [slab(0.88, 0.88), mound(0.3), ...poles(0.82, 0.82, 0.6)];
  if (s === 2) {
    return [slab(0.88, 0.88), mound(MOUND_H), ...adit(), ...headframe(), ...poles(0.82, 0.82, 1.0)];
  }
  return [
    slab(0.88, 0.88), mound(MOUND_H), ...adit(), hood(),
    ...headframe(), ...oreCart(), oreHeap(),
  ];
}

export const mineV3 = defineModel({
  id: "mine-v3",
  tiles: { w: 1, h: 1 },
  batchId: "45-optimized",
  name: "Mine (v3)",
  tags: ["building","mine","low-poly","age-of-mcp","v3"],
  summary: "Low-poly mine.",
  description: "A pit-head over a mountain seam that extracts ore on a slow timer.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench", position: [0, 0.12, 0.45], facing: Math.PI },
  ],
  interactions: [
    { id: "work", label: "Working the seam", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
